import { api } from '../slices/api';

// Kit Tracking ("tracked-card boards") endpoints, injected onto Fastlane's `api`
// slice so they reuse the shared baseQuery (VITE_API_URL + '/shop/api/'), Bearer
// auth and 401 handling.
//
// KT was a standalone app whose slice baseUrl was '/api/kit-staging/' and which
// reached staging/dekit areas via '../job-staging/…'. Ported here the URLs are
// relative to '/shop/api/':
//   KT '/api/kit-staging/X'  -> 'kit-staging/X'
//   KT '../job-staging/Y'    -> 'job-staging/Y'

export type ItemType = 'kit' | 'material';
export type Area = 'unassigned' | 'cart' | 'msa' | 'jsa' | 'dekit';

export interface TrackedCardJob {
	job_number: string;
	part_name: string;
	quantity: number | null;
	material: string;
	finish: string;
	thumbnail: string | null;
	machine_group: { name: string; color: string } | null;
	scheduled_start: string | null;
	est_machine_minutes: number | null;
	production_method: string;
	material_partial: boolean;
	kit_ready: boolean;
	material_ready: boolean;
	machine_setup_status?: string;
	machining_status?: string;
	kit_at_machine?: boolean;
	material_at_machine?: boolean;
	has_unpicked_dekit?: boolean;
	dekit_area_id: number | null;
	oli_uuid?: string | null;
}

export interface TrackedCard {
	id: number;
	// OLI UUID (backend changed this to a string). Used to pair kit + material.
	order_line_item_id: string;
	item_type: ItemType;
	current_area: Area;
	current_shelf: number | null;
	status: string;
	is_soft_hidden: boolean;
	last_cut_at: string | null;
	return_count: number;
	ticket: number | null;
	setup_pending: boolean;
	created_at: string;
	updated_at: string;
	status_explanation: string;
	pending_target_area?: string | null;
	pending_target_shelf?: number | null;
	pending_initiated_at?: string | null;
	pending_initiated_by?: string | null;
	job?: TrackedCardJob | null;
	picked_up?: boolean;
	priority?: number | null;
}

export interface StatusLegendEntry {
	status: string;
	label: string;
	description: string;
}

export interface Move {
	id: number;
	card: number;
	card_item_type?: ItemType;
	from_area: string;
	from_shelf: number | null;
	to_area: string;
	to_shelf: number | null;
	action: string;
	rule: string;
	operator: string;
	created_at: string;
}

export interface MoveFilter {
	card?: number;
	card__in?: string; // comma-separated card IDs
	oli?: string; // order_line_item_id — fetches across both kit+material cards
	action?: string;
	rule?: string;
	operator?: string;
	limit?: number;
}

export interface TrackedCardFilter {
	current_area?: Area;
	item_type?: ItemType;
	status?: string;
	order_line_item_id?: string;
}

export interface KtShelf {
	id: number;
	rack: number;
	position: number;
	is_active: boolean;
	is_occupied: boolean;
}

export interface KtRack {
	id: number;
	staging_area: number;
	staging_area_name: string;
	label: string;
	num_shelves: number;
	position_order: number;
	layout_column: number | null;
	layout_row: number | null;
	gap_after: boolean;
	shelves: KtShelf[];
}

export interface KtStagingArea {
	id: number;
	name: string;
	description: string;
	area_type: 'job_staging' | 'material_staging';
	is_active: boolean;
	machine_groups: { id: number; name: string; color: string }[];
	occupied_shelves: number;
	racks: KtRack[];
	route_order: number;
	total_shelves: number;
}

export interface KtDekitArea {
	id: number;
	name: string;
	description: string;
	machine_groups: { id: number; name: string; color: string }[];
	route_order: number;
	is_default_fallback: boolean;
	is_active: boolean;
}

// ─── Admin (areas / racks / dekit CRUD + dashboard) types ────────────────────

/** Richer machine-group shape used by the admin CRUD forms (includes code). */
export interface KitMachineGroup {
	id: number;
	name: string;
	code: string;
	color: string;
	is_active: boolean;
}

/** Staging-area list row as returned by the admin list endpoint — adds the
 *  derived counts (`rack_count`, `total_shelves`) the admin table renders. */
export interface KitStagingAreaListItem {
	id: number;
	name: string;
	description: string;
	area_type: 'job_staging' | 'material_staging';
	machine_groups: KitMachineGroup[];
	route_order: number;
	is_active: boolean;
	total_shelves: number;
	occupied_shelves: number;
	rack_count: number;
	serves_all_machine_groups?: boolean;
}

export interface KitDashboardStats {
	total_staging_areas: number;
	total_shelves: number;
	total_assigned: number;
	total_unassigned: number;
	ready_count: number;
	setup_pending_count: number;
	available_shelves: number;
}

export type MoveBody = {
	id: number;
	target_area: Area | 'machine';
	target_shelf?: number;
	operator?: string;
	reason?: string;
};

export type ConfirmMoveBody = {
	id: number;
	target_area?: Area | 'machine';
	target_shelf?: number;
	operator?: string;
	reason?: string;
};

// PTB-6615: merge the freshly-returned move card(s) into the board list
// cache so the assigned location + Confirm render instantly instead of
// waiting for the heavy full-board refetch. The move endpoints do not inject
// `card_priorities` (that is list-only), so preserve the list-computed
// `priority` to avoid a badge flicker until invalidatesTags reconciles.
export function mergeCardsIntoBoard(
	board: TrackedCard[],
	updates: TrackedCard[],
): void {
	for (const updated of updates) {
		const idx = board.findIndex((c) => c.id === updated.id);
		if (idx !== -1) {
			board[idx] = { ...updated, priority: board[idx].priority ?? updated.priority };
		}
	}
}

/**
 * Unwrap the `returnable` list response.
 *
 * The endpoint is paginated server-side (LargeResultsSetPagination, page_size
 * 800), so the body is `{count, next, previous, results}`. Both consumers --
 * ReturnableBoard and KitStagingTabletPage -- do `(cards ?? []).map(...)` and
 * would throw on the envelope, so it is unwrapped here and the query keeps its
 * `TrackedCard[]` contract.
 *
 * The array branch is deliberate rather than defensive noise: frontend and
 * backend deploy separately, so this has to work against both the old bare-list
 * response and the new envelope, in either deploy order.
 */
export const unwrapReturnable = (
	response: TrackedCard[] | { results?: TrackedCard[] } | null | undefined,
): TrackedCard[] => {
	if (Array.isArray(response)) return response;
	return response?.results ?? [];
};

const boardPatch = (cards: TrackedCard[]) =>
	kitStagingApi.util.updateQueryData('listTrackedCards', {}, (draft) => {
		mergeCardsIntoBoard(draft, cards);
	});

export const kitStagingApi = api.injectEndpoints({
	endpoints: (builder) => ({
		listTrackedCards: builder.query<TrackedCard[], TrackedCardFilter | void>({
			query: (params) => ({
				url: 'kit-staging/tracked-cards/',
				params: params ?? undefined,
			}),
			providesTags: (result) =>
				result
					? [
							...result.map((c) => ({ type: 'TrackedCard' as const, id: c.id })),
							{ type: 'TrackedCard' as const, id: 'LIST' },
						]
					: [{ type: 'TrackedCard' as const, id: 'LIST' }],
		}),
		getTrackedCard: builder.query<TrackedCard, number>({
			query: (id) => `kit-staging/tracked-cards/${id}/`,
			providesTags: (_r, _e, id) => [{ type: 'TrackedCard', id }],
		}),
		statusLegend: builder.query<StatusLegendEntry[], void>({
			query: () => 'kit-staging/status-legend/',
		}),
		listReturnable: builder.query<TrackedCard[], void>({
			query: () => 'kit-staging/returnable/',
			// Paginated server-side; see unwrapReturnable for why both shapes
			// are accepted.
			transformResponse: unwrapReturnable,
			providesTags: [{ type: 'TrackedCard', id: 'RETURNABLE' }],
		}),
		initiateMove: builder.mutation<TrackedCard, MoveBody>({
			query: ({ id, ...body }) => ({
				url: `kit-staging/tracked-cards/${id}/initiate-move/`,
				method: 'POST',
				body,
			}),
			invalidatesTags: (_r, _e, { id }) => [
				{ type: 'TrackedCard', id },
				{ type: 'TrackedCard', id: 'LIST' },
			],
			async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
				// Patch the returned card into the board so the assigned location
				// + Confirm render instantly instead of the heavy full-board
				// refetch (PTB-6615). invalidatesTags reconciles afterward.
				try {
					const { data } = await queryFulfilled;
					dispatch(boardPatch([data]));
				} catch {
					// Ignore: invalidatesTags still triggers a reconciling refetch.
				}
			},
		}),
		confirmMove: builder.mutation<TrackedCard, ConfirmMoveBody>({
			query: ({ id, ...body }) => ({
				url: `kit-staging/tracked-cards/${id}/confirm-move/`,
				method: 'POST',
				body,
			}),
			invalidatesTags: (_r, _e, { id }) => [
				{ type: 'TrackedCard', id },
				{ type: 'TrackedCard', id: 'LIST' },
			],
			async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
				// Patch the returned card into the board so the assigned location
				// + Confirm render instantly instead of the heavy full-board
				// refetch (PTB-6615). invalidatesTags reconciles afterward.
				try {
					const { data } = await queryFulfilled;
					dispatch(boardPatch([data]));
				} catch {
					// Ignore: invalidatesTags still triggers a reconciling refetch.
				}
			},
		}),
		undoMove: builder.mutation<TrackedCard, { id: number }>({
			query: ({ id }) => ({
				url: `kit-staging/tracked-cards/${id}/undo-move/`,
				method: 'POST',
			}),
			invalidatesTags: (_r, _e, { id }) => [
				{ type: 'TrackedCard', id },
				{ type: 'TrackedCard', id: 'LIST' },
			],
			async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
				// Patch the returned card into the board so the assigned location
				// + Confirm render instantly instead of the heavy full-board
				// refetch (PTB-6615). invalidatesTags reconciles afterward.
				try {
					const { data } = await queryFulfilled;
					dispatch(boardPatch([data]));
				} catch {
					// Ignore: invalidatesTags still triggers a reconciling refetch.
				}
			},
		}),
		deleteCard: builder.mutation<TrackedCard, { id: number }>({
			query: ({ id }) => ({
				url: `kit-staging/tracked-cards/${id}/delete/`,
				method: 'POST',
			}),
			invalidatesTags: (_r, _e, { id }) => [
				{ type: 'TrackedCard', id },
				{ type: 'TrackedCard', id: 'LIST' },
			],
		}),
		listMoves: builder.query<Move[], MoveFilter | void>({
			query: (params) => ({ url: 'kit-staging/moves/', params: params ?? undefined }),
		}),
		listStagingAreas: builder.query<KtStagingArea[], void>({
			query: () => 'job-staging/staging-areas/',
			providesTags: ['KitStagingAreas'],
		}),
		listDekitAreas: builder.query<KtDekitArea[], void>({
			query: () => 'job-staging/dekit-areas/',
			providesTags: ['KitDekitAreas'],
		}),
		markPickedUp: builder.mutation<TrackedCard & { updated: number }, { id: number }>({
			query: ({ id }) => ({
				url: `kit-staging/tracked-cards/${id}/mark-picked-up/`,
				method: 'POST',
			}),
			invalidatesTags: [{ type: 'TrackedCard', id: 'LIST' }],
		}),

		// One-tap batched Cart→JSA for a matched pair: moves the OLI's kit +
		// material together (initiate → confirm), atomically, server-side.
		movePair: builder.mutation<
			{ kit: TrackedCard; material: TrackedCard },
			{ order_line_item_id: string; action: 'initiate' | 'confirm' | 'undo' }
		>({
			query: (body) => ({
				url: 'kit-staging/tracked-cards/move-pair/',
				method: 'POST',
				body,
			}),
			invalidatesTags: [{ type: 'TrackedCard', id: 'LIST' }],
			async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
				// Patch BOTH returned cards into the board so the pair's assigned
				// location + Confirm render instantly instead of the heavy
				// full-board refetch (PTB-6615). invalidatesTags reconciles.
				try {
					const { data } = await queryFulfilled;
					dispatch(boardPatch([data.kit, data.material]));
				} catch {
					// Ignore: invalidatesTags still triggers a reconciling refetch.
				}
			},
		}),

		// ─── Admin: machine groups (read-only) ───────────────────────────────
		getKitMachineGroups: builder.query<KitMachineGroup[], void>({
			query: () => 'job-staging/machine-groups/',
			providesTags: ['KitMachineGroups'],
		}),

		// ─── Admin: staging areas CRUD ───────────────────────────────────────
		// (listStagingAreas above returns the lighter board read shape; the admin
		//  list view needs rack_count/total_shelves, so it has its own query.)
		getAdminStagingAreas: builder.query<KitStagingAreaListItem[], void>({
			query: () => 'job-staging/staging-areas/',
			providesTags: ['KitStagingAreas'],
		}),
		getAdminStagingArea: builder.query<KtStagingArea, number>({
			query: (id) => `job-staging/staging-areas/${id}/`,
			providesTags: (_r, _e, id) => [{ type: 'KitStagingAreas', id }],
		}),
		createStagingArea: builder.mutation<
			KtStagingArea,
			Partial<KtStagingArea> & { machine_group_ids?: number[] }
		>({
			query: (body) => ({
				url: 'job-staging/staging-areas/',
				method: 'POST',
				body,
			}),
			invalidatesTags: ['KitStagingAreas', 'KitDashboard'],
		}),
		updateStagingArea: builder.mutation<
			KtStagingArea,
			{ id: number; body: Partial<KtStagingArea> & { machine_group_ids?: number[] } }
		>({
			query: ({ id, body }) => ({
				url: `job-staging/staging-areas/${id}/`,
				method: 'PATCH',
				body,
			}),
			invalidatesTags: ['KitStagingAreas', 'KitDashboard'],
		}),
		deleteStagingArea: builder.mutation<void, number>({
			query: (id) => ({
				url: `job-staging/staging-areas/${id}/`,
				method: 'DELETE',
			}),
			invalidatesTags: ['KitStagingAreas', 'KitDashboard'],
		}),

		// ─── Admin: racks CRUD ───────────────────────────────────────────────
		// NOTE: names are prefixed "Kit" because inventory.ts defines its own
		// getRacks/createRack/... — duplicate endpoint names on the shared api
		// slice silently shadow each other (see endpoint-names.test.ts).
		getKitRacks: builder.query<KtRack[], number | void>({
			query: (stagingAreaId) =>
				stagingAreaId
					? `job-staging/racks/?staging_area=${stagingAreaId}`
					: 'job-staging/racks/',
			providesTags: ['KitRacks'],
		}),
		createKitRack: builder.mutation<KtRack, Partial<KtRack>>({
			query: (body) => ({ url: 'job-staging/racks/', method: 'POST', body }),
			invalidatesTags: ['KitRacks', 'KitStagingAreas', 'KitDashboard'],
		}),
		updateKitRack: builder.mutation<KtRack, { id: number; body: Partial<KtRack> }>({
			query: ({ id, body }) => ({
				url: `job-staging/racks/${id}/`,
				method: 'PATCH',
				body,
			}),
			invalidatesTags: ['KitRacks', 'KitStagingAreas', 'KitDashboard'],
		}),
		deleteKitRack: builder.mutation<void, number>({
			query: (id) => ({ url: `job-staging/racks/${id}/`, method: 'DELETE' }),
			invalidatesTags: ['KitRacks', 'KitStagingAreas', 'KitDashboard'],
		}),

		// ─── Admin: dekit areas CRUD ─────────────────────────────────────────
		// (listDekitAreas above is the board read; admin needs its own list to
		//  pair with create/update/delete invalidation.)
		getAdminDekitAreas: builder.query<KtDekitArea[], void>({
			query: () => 'job-staging/dekit-areas/',
			providesTags: ['KitDekitAreas'],
		}),
		createDekitArea: builder.mutation<
			KtDekitArea,
			Partial<KtDekitArea> & { machine_group_ids?: number[] }
		>({
			query: (body) => ({ url: 'job-staging/dekit-areas/', method: 'POST', body }),
			invalidatesTags: ['KitDekitAreas'],
		}),
		updateDekitArea: builder.mutation<
			KtDekitArea,
			{ id: number; body: Partial<KtDekitArea> & { machine_group_ids?: number[] } }
		>({
			query: ({ id, body }) => ({
				url: `job-staging/dekit-areas/${id}/`,
				method: 'PATCH',
				body,
			}),
			invalidatesTags: ['KitDekitAreas'],
		}),
		deleteDekitArea: builder.mutation<void, number>({
			query: (id) => ({ url: `job-staging/dekit-areas/${id}/`, method: 'DELETE' }),
			invalidatesTags: ['KitDekitAreas'],
		}),

		// ─── Admin: dashboard stats ──────────────────────────────────────────
		getKitDashboard: builder.query<KitDashboardStats, void>({
			query: () => 'job-staging/dashboard/',
			providesTags: ['KitDashboard'],
		}),
	}),
});

export const {
	useListTrackedCardsQuery,
	useGetTrackedCardQuery,
	useStatusLegendQuery,
	useListReturnableQuery,
	useInitiateMoveMutation,
	useConfirmMoveMutation,
	useUndoMoveMutation,
	useDeleteCardMutation,
	useListMovesQuery,
	useListStagingAreasQuery,
	useListDekitAreasQuery,
	useMarkPickedUpMutation,
	useMovePairMutation,
	// Admin CRUD
	useGetKitMachineGroupsQuery,
	useGetAdminStagingAreasQuery,
	useGetAdminStagingAreaQuery,
	useCreateStagingAreaMutation,
	useUpdateStagingAreaMutation,
	useDeleteStagingAreaMutation,
	useGetKitRacksQuery,
	useCreateKitRackMutation,
	useUpdateKitRackMutation,
	useDeleteKitRackMutation,
	useGetAdminDekitAreasQuery,
	useCreateDekitAreaMutation,
	useUpdateDekitAreaMutation,
	useDeleteDekitAreaMutation,
	useGetKitDashboardQuery,
} = kitStagingApi;
